import { useEffect, useRef, useState } from "react";
import { Paperclip, Send, Lock, FileText, X, Search } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fileUrl, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Pill, Thumb, EmptyState } from "@/components/Bits";

function Attachment({ a }) {
  const isImage = (a.content_type || "").startsWith("image/");
  if (!a.file_id)
    return (
      <div data-testid="project-message-attachment-name"
        className="mt-2 flex items-center gap-3 rounded-sm border border-dashed border-line bg-white px-3 py-2 text-xs text-fog">
        <FileText className="h-4 w-4" strokeWidth={1.5} />
        <span className="truncate">{a.filename} · sent from the website</span>
      </div>
    );
  return (
    <a href={fileUrl(a.file_id)} target="_blank" rel="noreferrer"
      data-testid={`project-message-attachment-${a.file_id}`}
      className="mt-2 flex items-center gap-3 rounded-sm border border-line bg-white px-3 py-2 text-xs text-stone transition-colors hover:border-fog">
      {isImage ? (
        <Thumb src={fileUrl(a.file_id)} alt={a.filename} className="h-10 w-12" />
      ) : (
        <span className="flex h-10 w-12 items-center justify-center rounded-sm border border-line bg-[#F1F0EC]">
          <FileText className="h-4 w-4 text-fog" strokeWidth={1.5} />
        </span>
      )}
      <span className="truncate">{a.filename}</span>
    </a>
  );
}

function Bubble({ m, mine }) {
  const initials = (m.author_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");
  return (
    <div data-testid={`project-message-${m.id}`} className={`flex gap-3 ${mine ? "flex-row-reverse" : ""}`}>
      <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ink text-[10px] font-medium text-white">
        {initials}
      </span>
      <div className={`max-w-[78%] ${mine ? "text-right" : ""}`}>
        <div className="mb-1 flex flex-wrap items-center gap-2 text-[11px] text-fog">
          <span className={mine ? "order-2" : ""}>{m.author_name}</span>
          <span className="num">{timeAgo(m.created_at)}</span>
          {m.internal && (
            <Pill tone="bg-[#F6DED6] text-[#A8492E]" testId={`project-message-internal-${m.id}`}>
              Internal only
            </Pill>
          )}
          {m.source === "website_admin" && (
            <Pill tone="bg-[#EDEFC4] text-ink" testId={`project-message-website-${m.id}`}>
              Sent from website admin
            </Pill>
          )}
        </div>
        {m.subject && <div className="mb-1 font-serif text-base text-ink">{m.subject}</div>}
        <div className={`inline-block whitespace-pre-wrap rounded-sm px-4 py-3 text-sm leading-relaxed ${
          m.internal ? "border border-dashed border-[#E0B4A5] bg-[#FBF1ED] text-ink"
            : mine ? "bg-ink text-white" : "border border-line bg-white text-ink"}`}>
          {m.body}
        </div>
        <div className={mine ? "flex flex-col items-end" : ""}>
          {(m.attachments || []).map((a, i) => <Attachment key={a.file_id || i} a={a} />)}
        </div>
      </div>
    </div>
  );
}

/** Project-scoped conversation. Internal is the default for staff so nothing
 *  reaches a client until it is deliberately marked client-facing. */
export default function ProjectMessages({ projectId }) {
  const { user, isStaff } = useAuth();
  const [thread, setThread] = useState(null);
  const [query, setQuery] = useState("");
  const [scope, setScope] = useState("all");
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [internal, setInternal] = useState(isStaff);
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const endRef = useRef();

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/messages`, {
        params: { ...(query.length >= 2 ? { q: query } : {}), ...(scope !== "all" ? { scope } : {}) },
      });
      setThread(data);
      if (!query && scope === "all") await api.post(`/projects/${projectId}/messages/read`, {});
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => { load(); }, [projectId, query, scope]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [thread]);

  const attach = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/projects/${projectId}/messages/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPending((p) => [...p, data]);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const send = async () => {
    if (!text.trim() && !pending.length) return;
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/messages`, {
        body: text.trim(), subject: subject.trim() || undefined,
        attachments: pending.map((p) => ({ file_id: p.file_id })), internal,
      });
      setText("");
      setSubject("");
      setPending([]);
      setInternal(isStaff);
      toast.success(internal ? "Internal note saved" : "Message sent to your client");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full rounded-sm border border-line bg-white px-3 py-2 text-sm outline-none transition-colors placeholder:text-fog focus:border-lime";

  return (
    <div className="space-y-5" data-testid="tab-messages">
      <div className="flex flex-wrap items-center gap-3">
        <label className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fog" strokeWidth={1.5} />
          <input data-testid="project-messages-search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search this conversation…" className={`${input} pl-9`} />
        </label>
        {isStaff && (
          <div className="flex gap-2" data-testid="project-messages-filter">
            {[["all", "All"], ["client", "Client-facing"], ["internal", "Internal"]].map(([v, l]) => (
              <button key={v} type="button" data-testid={`project-messages-scope-${v}`} onClick={() => setScope(v)}
                className={`rounded-sm border px-3 py-2 text-xs uppercase tracking-[0.12em] transition-colors ${
                  scope === v ? "border-lime bg-[#EDEFC4] text-ink" : "border-line bg-white text-stone hover:text-ink"}`}>
                {l}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="card-surface max-h-[55vh] space-y-6 overflow-auto p-6" data-testid="project-message-thread">
        {(thread?.messages || []).map((m) => (
          <Bubble key={m.id} m={m} mine={m.author_id === user?.id} />
        ))}
        {thread && thread.messages.length === 0 && (
          <EmptyState testId="project-thread-empty" title="Nothing here yet"
            body={query ? "No messages match that search." : "Start the conversation for this project."} />
        )}
        <div ref={endRef} />
      </div>

      <div className="card-surface space-y-3 p-4" data-testid="project-message-composer">
        {isStaff && (
          <input data-testid="project-message-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject (optional)" className={input} />
        )}
        <textarea data-testid="project-message-input" value={text} onChange={(e) => setText(e.target.value)} rows={3}
          placeholder={internal ? "Internal note — your client will not see this…" : "Write to your client…"}
          className={`${input} resize-none`} />
        {pending.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="project-pending-attachments">
            {pending.map((p) => (
              <span key={p.file_id} className="flex items-center gap-2 rounded-sm border border-line bg-white px-3 py-1.5 text-xs text-stone">
                <FileText className="h-3.5 w-3.5 text-fog" strokeWidth={1.5} />
                <span className="max-w-[160px] truncate">{p.filename}</span>
                <button data-testid={`project-remove-attachment-${p.file_id}`} aria-label="Remove attachment"
                  onClick={() => setPending((prev) => prev.filter((x) => x.file_id !== p.file_id))}>
                  <X className="h-3.5 w-3.5 text-fog hover:text-clay" strokeWidth={1.5} />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-sm border border-line px-3 py-2 text-xs text-stone transition-colors hover:border-fog">
              <Paperclip className="h-3.5 w-3.5" strokeWidth={1.5} />
              Attach
              <input ref={fileRef} data-testid="project-message-attach-input" type="file"
                accept="image/*,application/pdf" className="hidden" onChange={attach} disabled={busy} />
            </label>
            {isStaff && (
              <button type="button" data-testid="project-message-internal-toggle" onClick={() => setInternal((v) => !v)}
                className={`inline-flex items-center gap-2 rounded-sm border px-3 py-2 text-xs transition-colors ${
                  internal ? "border-[#E0B4A5] bg-[#FBF1ED] text-[#A8492E]" : "border-line text-stone hover:border-fog"}`}>
                <Lock className="h-3.5 w-3.5" strokeWidth={1.5} />
                {internal ? "Internal note" : "Visible to client"}
              </button>
            )}
          </div>
          <button type="button" data-testid="project-message-send" onClick={send} disabled={busy}
            className="inline-flex items-center gap-2 rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark disabled:opacity-50">
            <Send className="h-4 w-4" strokeWidth={1.5} />
            {busy ? "Sending…" : internal ? "Save note" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
